const debugSeenKeys = new Set();

function emitDebug(onDebug, payload) {
  if (typeof onDebug !== 'function') return;
  try {
    onDebug(payload);
  } catch (_) {
    // Ignore debug hook failures.
  }
}

function emitDebugOnce(onDebug, key, payload) {
  if (debugSeenKeys.has(key)) return;
  debugSeenKeys.add(key);
  emitDebug(onDebug, payload);
}

function getWebglEnumName(ctx, value) {
  if (!ctx || typeof value !== 'number') return String(value);
  try {
    const names = Object.keys(ctx).filter((key) => ctx[key] === value);
    return names[0] || String(value);
  } catch (_) {
    return String(value);
  }
}

function patchWebGLContext(ctx, canvas, vendorName, onDebug) {
  if (!ctx || ctx.__storyRuntimePatched) return ctx;

  if (typeof ctx.getShaderPrecisionFormat === 'function') {
    const originalGetShaderPrecisionFormat = ctx.getShaderPrecisionFormat.bind(ctx);
    ctx.getShaderPrecisionFormat = (...args) => {
      try {
        const result = originalGetShaderPrecisionFormat(...args);
        if (result && typeof result.precision === 'number') return result;
        emitDebugOnce(onDebug, `shader-precision-null:${args.join(':')}`, {
          phase: 'webgl-null-shader-precision',
          shaderType: getWebglEnumName(ctx, args[0]),
          precisionType: getWebglEnumName(ctx, args[1]),
        });
      } catch (_) {
        emitDebugOnce(onDebug, `shader-precision-error:${args.join(':')}`, {
          phase: 'webgl-shader-precision-error',
          shaderType: getWebglEnumName(ctx, args[0]),
          precisionType: getWebglEnumName(ctx, args[1]),
        });
      }
      return { rangeMin: 127, rangeMax: 127, precision: 23 };
    };
  }

  if (typeof ctx.getParameter === 'function') {
    const originalGetParameter = ctx.getParameter.bind(ctx);
    ctx.getParameter = (pname) => {
      try {
        const result = originalGetParameter(pname);
        if (result != null) return result;
        emitDebugOnce(onDebug, `get-parameter-null:${pname}`, {
          phase: 'webgl-get-parameter-null',
          pname: getWebglEnumName(ctx, pname),
        });
      } catch (_) {
        emitDebugOnce(onDebug, `get-parameter-error:${pname}`, {
          phase: 'webgl-get-parameter-error',
          pname: getWebglEnumName(ctx, pname),
        });
      }

      const isWebGL2 =
        typeof window !== 'undefined'
        && typeof window.WebGL2RenderingContext !== 'undefined'
        && ctx instanceof window.WebGL2RenderingContext;

      if (pname === ctx.VERSION) return isWebGL2 ? 'WebGL 2.0' : 'WebGL 1.0';
      if (pname === ctx.SHADING_LANGUAGE_VERSION) return isWebGL2 ? 'WebGL GLSL ES 3.00' : 'WebGL GLSL ES 1.00';
      if (pname === ctx.VENDOR) return vendorName;
      if (pname === ctx.RENDERER) return vendorName;
      if (pname === ctx.VIEWPORT || pname === ctx.SCISSOR_BOX) {
        return [0, 0, Math.max(1, canvas?.width || 1), Math.max(1, canvas?.height || 1)];
      }
      if (pname === ctx.COLOR_CLEAR_VALUE || pname === ctx.BLEND_COLOR) return [0, 0, 0, 0];
      if (pname === ctx.COLOR_WRITEMASK) return [true, true, true, true];
      if (pname === ctx.DEPTH_WRITEMASK) return true;
      if (pname === ctx.DEPTH_CLEAR_VALUE) return 1;
      if (pname === ctx.STENCIL_CLEAR_VALUE) return 0;
      if (pname === ctx.STENCIL_WRITEMASK || pname === ctx.STENCIL_BACK_WRITEMASK) return 0xffffffff;
      if (pname === ctx.CULL_FACE_MODE) return ctx.BACK;
      if (pname === ctx.FRONT_FACE) return ctx.CCW;
      if (pname === ctx.DEPTH_FUNC) return ctx.LESS;
      if (pname === ctx.BLEND_SRC_RGB || pname === ctx.BLEND_SRC_ALPHA) return ctx.ONE;
      if (pname === ctx.BLEND_DST_RGB || pname === ctx.BLEND_DST_ALPHA) return ctx.ZERO;
      if (pname === ctx.BLEND_EQUATION_RGB || pname === ctx.BLEND_EQUATION_ALPHA) return ctx.FUNC_ADD;
      if (pname === ctx.ACTIVE_TEXTURE) return ctx.TEXTURE0;
      if (pname === ctx.IMPLEMENTATION_COLOR_READ_FORMAT) return ctx.RGBA;
      if (pname === ctx.IMPLEMENTATION_COLOR_READ_TYPE) return ctx.UNSIGNED_BYTE;
      if (pname === ctx.MAX_TEXTURE_SIZE
        || pname === ctx.MAX_CUBE_MAP_TEXTURE_SIZE
        || pname === ctx.MAX_RENDERBUFFER_SIZE) return 4096;
      if (pname === ctx.MAX_TEXTURE_IMAGE_UNITS
        || pname === ctx.MAX_VERTEX_TEXTURE_IMAGE_UNITS
        || pname === ctx.MAX_COMBINED_TEXTURE_IMAGE_UNITS) return 16;
      if (pname === ctx.MAX_VERTEX_ATTRIBS) return 16;
      if (pname === ctx.MAX_VARYING_VECTORS) return 16;
      if (pname === ctx.MAX_VERTEX_UNIFORM_VECTORS || pname === ctx.MAX_FRAGMENT_UNIFORM_VECTORS) return 1024;
      if (pname === ctx.MAX_UNIFORM_BUFFER_BINDINGS) return 24;
      if (pname === ctx.RED_BITS || pname === ctx.GREEN_BITS || pname === ctx.BLUE_BITS || pname === ctx.ALPHA_BITS) return 8;
      if (pname === ctx.DEPTH_BITS) return 24;
      if (pname === ctx.STENCIL_BITS) return 8;
      if (pname === ctx.SUBPIXEL_BITS) return 4;
      if (pname === ctx.SAMPLES || pname === ctx.MAX_SAMPLES) return 4;
      if (pname === ctx.UNPACK_ROW_LENGTH
        || pname === ctx.UNPACK_IMAGE_HEIGHT
        || pname === ctx.UNPACK_SKIP_PIXELS
        || pname === ctx.UNPACK_SKIP_ROWS
        || pname === ctx.UNPACK_SKIP_IMAGES) return 0;
      return null;
    };
  }

  if (typeof ctx.getSupportedExtensions === 'function') {
    const originalGetSupportedExtensions = ctx.getSupportedExtensions.bind(ctx);
    ctx.getSupportedExtensions = (...args) => {
      try {
        const result = originalGetSupportedExtensions(...args);
        if (Array.isArray(result)) return result;
        emitDebugOnce(onDebug, 'supported-extensions-null', {
          phase: 'webgl-supported-extensions-null',
        });
        return [];
      } catch (_) {
        emitDebugOnce(onDebug, 'supported-extensions-error', {
          phase: 'webgl-supported-extensions-error',
        });
        return [];
      }
    };
  }

  if (typeof ctx.getExtension === 'function') {
    const originalGetExtension = ctx.getExtension.bind(ctx);
    ctx.getExtension = (name) => {
      try {
        const result = originalGetExtension(name);
        if (result != null) return result;
        emitDebugOnce(onDebug, `get-extension-null:${name}`, {
          phase: 'webgl-get-extension-null',
          extension: String(name),
        });
        return null;
      } catch (_) {
        emitDebugOnce(onDebug, `get-extension-error:${name}`, {
          phase: 'webgl-get-extension-error',
          extension: String(name),
        });
        return null;
      }
    };
  }

  if (typeof ctx.getShaderInfoLog === 'function') {
    const originalGetShaderInfoLog = ctx.getShaderInfoLog.bind(ctx);
    ctx.getShaderInfoLog = (...args) => {
      try {
        return originalGetShaderInfoLog(...args) ?? '';
      } catch (_) {
        return '';
      }
    };
  }

  if (typeof ctx.getProgramInfoLog === 'function') {
    const originalGetProgramInfoLog = ctx.getProgramInfoLog.bind(ctx);
    ctx.getProgramInfoLog = (...args) => {
      try {
        return originalGetProgramInfoLog(...args) ?? '';
      } catch (_) {
        return '';
      }
    };
  }

  ctx.__storyRuntimePatched = true;
  return ctx;
}

export function ensureSafeStoryWebGLRuntime({ vendorName = 'StoryRuntime', onDebug } = {}) {
  if (typeof window === 'undefined') return;
  if (window.__storyRuntimeWebglPatched) {
    emitDebug(onDebug, { phase: 'webgl-precision-patched', reused: true });
    return;
  }

  const patchPrototype = (Ctor) => {
    if (!Ctor?.prototype || typeof Ctor.prototype.getShaderPrecisionFormat !== 'function') return;
    const original = Ctor.prototype.getShaderPrecisionFormat;
    Ctor.prototype.getShaderPrecisionFormat = function patchedGetShaderPrecisionFormat(...args) {
      try {
        const result = original.apply(this, args);
        if (result && typeof result.precision === 'number') return result;
      } catch (_) {
        // Fall through to a conservative default for embedded runtimes.
      }
      return { rangeMin: 127, rangeMax: 127, precision: 23 };
    };
  };

  patchPrototype(window.WebGLRenderingContext);
  patchPrototype(window.WebGL2RenderingContext);

  if (window.HTMLCanvasElement?.prototype?.getContext && !window.HTMLCanvasElement.prototype.__storyRuntimeGetContextPatched) {
    const originalGetContext = window.HTMLCanvasElement.prototype.getContext;
    window.HTMLCanvasElement.prototype.getContext = function patchedGetContext(type, ...args) {
      const ctx = originalGetContext.call(this, type, ...args);
      if (/^webgl2?$|^experimental-webgl$/.test(String(type))) {
        emitDebug(onDebug, {
          phase: 'canvas-get-context',
          type: String(type),
          hasContext: Boolean(ctx),
        });
        return patchWebGLContext(ctx, this, vendorName, onDebug);
      }
      return ctx;
    };
    window.HTMLCanvasElement.prototype.__storyRuntimeGetContextPatched = true;
  }

  window.__storyRuntimeWebglPatched = true;
  emitDebug(onDebug, { phase: 'webgl-precision-patched' });
}
